'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

export default function SentryInit() {
  useEffect(() => {
    // Check if Sentry is already initialized by instrumentation-client.ts
    if (Sentry.getClient()) {
      return; // Sentry already initialized, skip
    }

    // Only initialize Sentry in production or staging environments
    // Since this is client-side, we need to check for environment indicators
    const isStaging =
      (typeof window !== 'undefined' &&
        window.location.hostname.includes('staging')) ||
      process.env.VERCEL_ENV === 'preview';
    const isLocalDevelopment =
      process.env.NODE_ENV === 'development' && !process.env.VERCEL_ENV;

    if (isLocalDevelopment) {
      return; // Skip Sentry initialization in local development
    }

    // Determine environment for Sentry
    let environment = 'production';
    if (isStaging) {
      environment = 'staging';
    }

    // Initialize Sentry with direct configuration
    Sentry.init({
      dsn: 'https://6c78e8d97e627746b9e0102482cd1bf4@o136413.ingest.us.sentry.io/4509385983197184',
      tracesSampleRate: 1,
      debug: false, // Disable debug for production
      environment,

      beforeSend(event) {
        // Get current user context from the global scope
        const currentUser = Sentry.getCurrentScope().getUser();

        if (currentUser && Object.keys(currentUser).length > 0) {
          // Ensure user context is attached to the event
          event.user = currentUser;
        } else {
          // Try to get user from fallback sources
          try {
            if (typeof window !== 'undefined') {
              // First try globally stored user data
              const globalUserData = (
                window as { __SENTRY_USER_DATA__?: Record<string, unknown> }
              ).__SENTRY_USER_DATA__;
              if (globalUserData) {
                event.user = globalUserData;
              } else {
                // Then try auth store
                const authModule = (
                  window as {
                    __AUTH_MODULE__?: {
                      useAuthStore: {
                        getState: () => {
                          isAuthenticated: boolean;
                          user?: { id: string; email: string };
                          pilot?: {
                            id: string;
                            nome: string;
                            categoria: string;
                          };
                        };
                      };
                    };
                  }
                ).__AUTH_MODULE__;
                if (authModule) {
                  const store = authModule.useAuthStore.getState();
                  if (store.isAuthenticated && store.user) {
                    const userData = {
                      id: store.user.id,
                      email: store.user.email,
                      username: store.pilot?.nome || store.user.email,
                      ...(store.pilot && {
                        pilot_id: store.pilot.id,
                        pilot_name: store.pilot.nome,
                        pilot_category: store.pilot.categoria,
                      }),
                    };
                    event.user = userData;
                  }
                }
              }
            }
          } catch (error) {
            // Silently fail - don't break error reporting if user context fails
            console.warn('Failed to get user context for Sentry:', error);
          }
        }

        return event;
      },
    });
  }, []);

  return null; // This component doesn't render anything
}
